import React, { useState, useRef, useEffect } from "react";
import { t } from "i18next";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Card,
  CardBody,
  Label,
} from "reactstrap";
import { QRCode } from "react-qrcode-logo";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { convertQRToBlob } from "../../../../utils/qrDownload";

// Add custom styles for hover effect
const customButtonStyles = `
  .hover-bg-custom:hover {
    background-color: #c59253 !important;
    border-color: #c59253 !important;
    color: white !important;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = customButtonStyles;
  document.head.appendChild(styleSheet);
}

const DownloadQRModal = ({ isOpen, toggle, spaces, siteName, siteImage }) => {
  const [selectedSpaces, setSelectedSpaces] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const qrRefs = useRef({});

  useEffect(() => {
    if (isOpen) {
      setSelectedSpaces([]);
      setSelectAll(false);
    }
  }, [isOpen]);

  const toggleSpaceSelection = (spaceId) => {
    setSelectedSpaces((prev) => {
      if (prev.includes(spaceId)) {
        return prev.filter((id) => id !== spaceId);
      } else {
        return [...prev, spaceId];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedSpaces([]);
    } else {
      setSelectedSpaces(spaces.map((space) => space.space_id));
    }
    setSelectAll(!selectAll);
  };


  const downloadSingleQR = async (space) => {
    setIsDownloading(true);
    try {
      const qrElement = qrRefs.current[space.space_id];
      if (!qrElement) {
        console.error("QR element not found for space:", space.space_id);
        return;
      }

      const blob = await convertQRToBlob(qrElement, space.name);
      if (blob) {
        const fileName = `${space.name}_${space.space_id}.png`;
        saveAs(blob, fileName);
      } else {
        console.error("Failed to generate blob for space:", space.space_id);
        alert(t("Failed to download QR code. The logo image may be blocked by CORS policy."));
      }
    } catch (error) {
      console.error("Error downloading QR code:", error);
      alert(t("Failed to download QR code. Please try again."));
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadSelectedQRs = async () => {
    if (selectedSpaces.length === 0) return;

    setIsDownloading(true);

    try {
      if (selectedSpaces.length === 1) {
        // Download single QR code
        const space = spaces.find((s) => s.space_id === selectedSpaces[0]);
        if (space) {
          await downloadSingleQR(space);
        }
      } else {
        // Download multiple QR codes as ZIP
        const zip = new JSZip();
        let successCount = 0;

        for (const spaceId of selectedSpaces) {
          const space = spaces.find((s) => s.space_id === spaceId);
          if (!space) continue;

          const qrElement = qrRefs.current[spaceId];
          if (!qrElement) continue;

          const blob = await convertQRToBlob(qrElement, space.name);
          if (blob) {
            const fileName = `${space.name}_${space.space_id}.png`;
            zip.file(fileName, blob);
            successCount++;
          }
        }

        if (successCount === 0) {
          alert(t("Failed to generate any QR codes. The logo images may be blocked by CORS policy."));
          return;
        }

        const zipBlob = await zip.generateAsync({ type: "blob" });
        const zipFileName = `${siteName || "QR_Codes"}.zip`;
        saveAs(zipBlob, zipFileName);

        if (successCount < selectedSpaces.length) {
          alert(t(`Successfully downloaded ${successCount} out of ${selectedSpaces.length} QR codes.`));
        }
      }

      // Clear selection after download
      setSelectedSpaces([]);
      setSelectAll(false);
    } catch (error) {
      console.error("Error downloading QR codes:", error);
      alert(t("Failed to download QR codes. Please try again."));
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadAllQRs = async () => {
    setIsDownloading(true);

    try {
      const zip = new JSZip();
      let successCount = 0;
      let totalCount = 0;

      for (const space of spaces) {
        if (!space.qrKey) continue;
        totalCount++;

        const qrElement = qrRefs.current[space.space_id];
        if (!qrElement) continue;

        const blob = await convertQRToBlob(qrElement, space.name);
        if (blob) {
          const fileName = `${space.name}_${space.space_id}.png`;
          zip.file(fileName, blob);
          successCount++;
        }
      }

      if (successCount === 0) {
        alert(t("Failed to generate any QR codes. The logo images may be blocked by CORS policy."));
        return;
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const zipFileName = `${siteName || "All_QR_Codes"}.zip`;
      saveAs(zipBlob, zipFileName);

      if (successCount < totalCount) {
        alert(t(`Successfully downloaded ${successCount} out of ${totalCount} QR codes.`));
      }
    } catch (error) {
      console.error("Error downloading all QR codes:", error);
      alert(t("Failed to download QR codes. Please try again."));
    } finally {
      setIsDownloading(false);
    }
  };

  const spacesWithQR = spaces.filter((space) => space.qrKey);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>{t("Download QR Codes")}</ModalHeader>
      <ModalBody>
        {spacesWithQR.length === 0 ? (
          <div className="text-center p-4">
            <p className="text-muted">{t("No spaces with QR codes found")}</p>
          </div>
        ) : (
          <>
            <div className="mb-3 d-flex justify-content-between align-items-center">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={selectAll}
                  onChange={toggleSelectAll}
                  id="selectAll"
                />
                <Label className="form-check-label" htmlFor="selectAll">
                  {t("Select All")} ({spacesWithQR.length} {t("spaces")})
                </Label>
              </div>
              <div>
                <Button
                  size="sm"
                  onClick={downloadAllQRs}
                  disabled={isDownloading}
                  className="me-2 text-white"
                  style={{ backgroundColor: "#c59253", borderColor: "#c59253" }}
                >
                  {isDownloading ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <i className="ri-download-2-line me-1"></i>
                      {t("Download All")}
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div
              style={{
                maxHeight: "500px",
                overflowY: "auto",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
                padding: "10px",
              }}
            >
              {spacesWithQR.map((space) => (
                <Card key={space.space_id} className="mb-2">
                  <CardBody className="p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center flex-grow-1">
                        <input
                          className="form-check-input me-3"
                          type="checkbox"
                          checked={selectedSpaces.includes(space.space_id)}
                          onChange={() => toggleSpaceSelection(space.space_id)}
                        />
                        <div className="me-3">
                          {/* Hidden QR Code for download */}
                          <div
                            ref={(el) => (qrRefs.current[space.space_id] = el)}
                            style={{ display: "none" }}
                          >
                            <QRCode
                              value={`${process.env.REACT_APP_QR_URL}/menu/${space.qrKey}`}
                              size={512}
                              eyeRadius={10}
                              ecLevel="H"
                              logoPaddingStyle="circle"
                              bgColor="#fff"
                              // logoImage={space?.menu_site_image || siteImage}
                              enableCORS={true}
                              crossOrigin="anonymous"
                            />
                          </div>
                          {/* Visible preview QR Code */}
                          <QRCode
                            value={`${process.env.REACT_APP_QR_URL}/menu/${space.qrKey}`}
                            size={60}
                            eyeRadius={5}
                            ecLevel="H"
                            logoPaddingStyle="circle"
                            bgColor="#fff"
                            // logoImage={space?.menu_site_image || siteImage}
                            enableCORS={true}
                            crossOrigin="anonymous"
                          />
                        </div>
                        <div>
                          <h6 className="mb-1">
                            {space.name}
                            {space.name_ar && ` (${space.name_ar})`}
                          </h6>
                          <small className="text-muted">
                            ID: {space.space_id} | Type: {space.type}
                          </small>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        outline
                        onClick={() => downloadSingleQR(space)}
                        disabled={isDownloading}
                        style={{ borderColor: "#c59253", color: "#c59253" }}
                        className="hover-bg-custom"
                      >
                        <i className="ri-download-line"></i>
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={downloadSelectedQRs}
          disabled={selectedSpaces.length === 0 || isDownloading}
          className="text-white"
          style={{ backgroundColor: "#c59253", borderColor: "#c59253" }}
        >
          {isDownloading ? (
            <Spinner size="sm" />
          ) : (
            <>
              <i className="ri-download-2-line me-1"></i>
              {t("Download Selected")} ({selectedSpaces.length})
            </>
          )}
        </Button>
        <Button color="secondary" onClick={toggle} disabled={isDownloading}>
          {t("Cancel")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default DownloadQRModal;

